import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button, Callout } from '../../../components/ui';
import type { EmployeeView } from '../shared';

interface EmployeeAssistantProps {
  employee: EmployeeView;
  onAction?: (action: string) => void;
}

interface Suggestion {
  body: React.ReactNode;
  actions: Array<{ label: string; primary?: boolean; key: string }>;
}

function suggest(employee: EmployeeView): Suggestion | null {
  const first = employee.name.split(' ')[0];
  if (employee.status === 'blocked' && employee.blockerShort) {
    return {
      body: (
        <>
          {first} is blocked on <strong>{employee.blockerShort}</strong>. I can route an admin request and unblock —
          continue?
        </>
      ),
      actions: [
        { key: 'route', label: 'Route grant request', primary: true },
        { key: 'plan', label: 'Show plan' },
        { key: 'dismiss', label: 'Dismiss' },
      ],
    };
  }
  if (employee.status === 'review') {
    return {
      body: (
        <>
          A pending decision is waiting for you on {first}'s output. <strong>Open side-by-side review?</strong>
        </>
      ),
      actions: [
        { key: 'open-review', label: 'Open review', primary: true },
        { key: 'dismiss', label: 'Dismiss' },
      ],
    };
  }
  if (employee.status === 'paused' && employee.cost7d > 30) {
    return {
      body: (
        <>
          {first} was paused after a cost spike. <strong>Tighten budget and resume</strong>, or terminate?
        </>
      ),
      actions: [
        { key: 'resume', label: 'Tighten + resume', primary: true },
        { key: 'terminate', label: 'Terminate' },
        { key: 'dismiss', label: 'Dismiss' },
      ],
    };
  }
  if (employee.status === 'setup') {
    return {
      body: (
        <>
          A permission grant expires soon. <strong>Renew now</strong> to avoid blocking the next scheduled action.
        </>
      ),
      actions: [
        { key: 'renew', label: 'Renew grant', primary: true },
        { key: 'dismiss', label: 'Dismiss' },
      ],
    };
  }
  return null;
}

export const EmployeeAssistant: React.FC<EmployeeAssistantProps> = ({ employee, onAction }) => {
  const [dismissed, setDismissed] = useState(false);
  if (!onAction) return null;
  if (dismissed) return null;
  const sug = suggest(employee);
  if (!sug) return null;
  return (
    <Callout
      tone="purple"
      icon={Sparkles}
      title="Assistant"
      actions={
        <div className="flex flex-wrap gap-1.5">
          {sug.actions.map((a) => (
            <Button
              key={a.key}
              size="sm"
              variant={a.primary ? 'primary' : a.key === 'dismiss' ? 'ghost' : 'secondary'}
              onClick={() => {
                if (a.key === 'dismiss') {
                  setDismissed(true);
                  return;
                }
                onAction(a.key);
              }}
            >
              {a.label}
            </Button>
          ))}
        </div>
      }
    >
      {sug.body}
    </Callout>
  );
};
